import React from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { money } from "utils/helpers";

const DONUT_COLORS = ["#22c55e", "#3b82f6"];

function MiniLineChart({ title, points, color }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-2 text-sm font-semibold text-gray-700">{title}</h3>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={points}>
          <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis hide />
          <Tooltip formatter={(v) => ['Rp ' + money(v), 'Omzet']} />
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function SalesVsRentalDonut({ salesPercent, rentalPercent, total }) {
  const chartData = [
    { name: "Sales", value: salesPercent ?? 0 },
    { name: "Rental", value: rentalPercent ?? 0 },
  ];

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-2 text-sm font-semibold text-gray-700">Sales vs Rental</h3>
      <div className="relative">
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              innerRadius={50}
              outerRadius={75}
              paddingAngle={2}
            >
              {chartData.map((_, i) => (
                <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[11px] text-gray-400">Total</span>
          <span className="text-sm font-semibold text-gray-900">{money(total)}</span>
        </div>
      </div>
      <div className="mt-2 flex justify-center gap-4 text-xs">
        <span className="flex items-center gap-1 text-gray-600">
          <span className="h-2 w-2 rounded-full bg-emerald-500" /> Sales {salesPercent?.toFixed(1)}%
        </span>
        <span className="flex items-center gap-1 text-gray-600">
          <span className="h-2 w-2 rounded-full bg-blue-500" /> Rental {rentalPercent?.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

function TopProductsList({ products }) {
  const maxQty = Math.max(...(products?.map((p) => p.qty) ?? [1]));

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-gray-700">Top 5 Best Selling Products</h3>
      <div className="space-y-3">
        {products?.map((p) => (
          <div key={p.productName} className="text-xs">
            <div className="mb-1 flex justify-between">
              <span className="text-gray-600">{p.productName}</span>
              <span className="font-medium text-gray-900">{money(p.qty)}</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100">
              <div
                className="h-1.5 rounded-full bg-emerald-500"
                style={{ width: `${(p.qty / maxQty) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopRentalProducts({ products }) {
  const maxQty = Math.max(...(products?.map((p) => p.qty) ?? [1]));

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-gray-700">Top 5 Best Rental Products</h3>
      <div className="space-y-3">
        {products?.map((p) => (
          <div key={p.productName} className="text-xs">
            <div className="mb-1 flex justify-between">
              <span className="text-gray-600">{p.productName}</span>
              <span className="font-medium text-gray-900">{money(p.qty)}</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100">
              <div
                className="h-1.5 rounded-full bg-emerald-500"
                style={{ width: `${(p.qty / maxQty) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ChartsSection({ data, loading, error }) {
  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
        {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-52 animate-pulse rounded-xl bg-gray-100" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <MiniLineChart title="Daily Sales" points={data?.dailySales} />
          <MiniLineChart title="Daily Rental" points={data?.dailyRental} />

          <SalesVsRentalDonut
              salesPercent={data?.salesPercent}
              rentalPercent={data?.rentalPercent}
              total={data?.salesVsRentalTotal}
          />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <TopProductsList products={data?.topSellingProducts} />
          <TopRentalProducts products={data?.topRentalProducts} />
      </div>
    </div>
  );
}